import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { rendererClient } from "../../lib/rendererClient";
import { languageKeys } from "@op/puck-types";
import { ProjectPage } from "@op/puck";
import { ContentProvider, TranslationProvider } from "@op/utils";
import { useTranslations } from "../../utils/translations";
import { contextSchema, type Context } from "../../utils/context";

const paramsSchema = z.object({
  languageKey: z.enum(languageKeys),
});

const getLanguage = createServerFn({
  method: "GET",
})
  .inputValidator(z.object({ params: paramsSchema, context: contextSchema }))
  .handler(
    async ({
      data: {
        params: { languageKey },
        context: { organizationSlug, projectSlug },
      },
    }) => {
      const res = await rendererClient.languages[":organizationSlug"][
        ":projectSlug"
      ][":languageKey"].$get({
        param: {
          organizationSlug,
          projectSlug,
          languageKey,
        },
      });
      if (!res.ok) {
        if (res.status === 404) throw notFound();
        throw new Error("Failed to fetch language topics");
      }
      const data = await res.json();
      return data;
    },
  );

export const Route = createFileRoute("/$languageKey/")({
  component: RouteComponent,
  params: paramsSchema,
  beforeLoad: ({ context: { languages }, params: { languageKey } }) => {
    return { languages: languages.filter((lang) => lang !== languageKey) };
  },
  loader: async ({ params, context }) => {
    const topics = await getLanguage({
      data: {
        params,
        context: context as Context,
      },
    });

    return topics;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [
        { title: `${loaderData.projectName} | ${loaderData.organizationName}` },
      ],
    };
  },
});

function RouteComponent() {
  const { languageKey } = Route.useParams();
  const { languages } = Route.useRouteContext();
  const {
    topics,
    imprintLink,
    privacyLink,
    copyrightText,
    hasImprint,
    hasPrivacyPolicy,
    heading,
    subheading,
    brandElement,
    logoImage,
    organizationName,
    organizationLink,
  } = Route.useLoaderData();
  const translations = useTranslations();

  return (
    <ContentProvider
      citations={{ content: [], excursions: {} }}
      glossaryItems={[]}
      excursions={[]}
      versions={[]}
      outline={{ content: [], excursions: {} }}
      figures={{ content: [], excursions: {} }}
    >
      <TranslationProvider {...translations}>
        <ProjectPage
          topics={topics}
          languageKey={languageKey}
          languages={languages}
          imprintLink={imprintLink ?? undefined}
          privacyLink={privacyLink ?? undefined}
          copyrightText={copyrightText ?? undefined}
          hasImprint={hasImprint}
          hasPrivacyPolicy={hasPrivacyPolicy}
          heading={heading ?? undefined}
          subheading={subheading ?? undefined}
          brandElement={brandElement ?? undefined}
          logoImage={logoImage ?? undefined}
          organizationName={organizationName ?? undefined}
          organizationLink={organizationLink ?? undefined}
          LinkComponent={Link}
        />
      </TranslationProvider>
    </ContentProvider>
  );
}
